import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Loader2 } from "lucide-react";
import { fmtMoney } from "@/lib/pricing";
import { toast } from "sonner";

export const QUOTE_STATUSES = ["Draft", "Sent", "Accepted", "Declined"];

const inputCls =
  "focus-visible:ring-2 focus-visible:ring-[#F97316]/30 focus-visible:border-[#F97316]";

export default function SaveQuoteModal({ open, onOpenChange, agentName, mowerType, price, method, defaultClientName, onSave, saving }) {
  const [clientName, setClientName] = useState("");
  const [status, setStatus] = useState("Draft");
  const [addonNote, setAddonNote] = useState("");

  useEffect(() => {
    if (open && defaultClientName) {
      setClientName((c) => c || defaultClientName);
    }
  }, [open, defaultClientName]);

  const resetAndClose = (v) => {
    if (!v) {
      setClientName("");
      setStatus("Draft");
      setAddonNote("");
    }
    onOpenChange(v);
  };

  const handleSave = async () => {
    if (!clientName.trim()) {
      toast.error("Enter the client name");
      return;
    }
    const ok = await onSave({ clientName: clientName.trim(), status, addonNote: addonNote.trim() });
    if (ok) {
      setClientName("");
      setStatus("Draft");
      setAddonNote("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#1C1917]">Save Quote</DialogTitle>
          <DialogDescription>
            Saved with the agent, client, equipment, pricing, status and a PDF copy of the customer quote.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md bg-stone-50 border border-border p-3 text-xs text-[#57534E] space-y-1">
            <div>
              Agent: <span className="font-semibold text-[#1C1917]">{agentName || "—"}</span>
            </div>
            <div>
              Equipment: <span className="font-semibold text-[#1C1917]">{mowerType || "—"}</span>
            </div>
            <div>
              Price: <span className="font-semibold text-[#EA580C]">{fmtMoney(price)}</span>{" "}
              <span className="text-stone-400">({method})</span>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.1em] text-[#57534E]">Client Name</Label>
            <Input
              className={`mt-1.5 ${inputCls}`}
              placeholder="Customer or business name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              data-testid="save-quote-client-name"
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.1em] text-[#57534E]">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className={`mt-1.5 ${inputCls}`} data-testid="save-quote-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUOTE_STATUSES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`save-quote-status-${s.toLowerCase()}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.1em] text-[#57534E]">
              Possible Route Add-On <span className="normal-case font-normal">(optional)</span>
            </Label>
            <Textarea
              className={`mt-1.5 ${inputCls}`}
              rows={2}
              placeholder="e.g. Can combine with the Tulsa delivery next week to cut shipping cost"
              value={addonNote}
              onChange={(e) => setAddonNote(e.target.value)}
              data-testid="save-quote-addon-note"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => resetAndClose(false)} data-testid="save-quote-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold"
              data-testid="save-quote-confirm"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Quote
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
